/**
 * useDentalSelection — extracted from DentalChartV2.
 * ==================================================
 * Pure state hook that owns every "which teeth are currently selected"
 * concern for the Dental Chart: single click, Ctrl/Cmd toggle, Shift range,
 * quadrants, arches, invert, "select all healthy / caries / RCT / …", etc.
 *
 * Every public helper is stable (`useCallback`) so passing them straight
 * into `<ToothV2 onSelect={handleToothClick}>` doesn't break memoisation.
 *
 * Inputs
 * ------
 *   allTeeth    — ordered array of FDI numbers for the current dentition
 *   chartTeeth  — map of `{ [fdi]: { conditions: [{code, surfaces}] } }`,
 *                 needed only by the smart filters ("All Caries", "Healthy",
 *                 etc.). Pass the latest value on every render; the callbacks
 *                 depend on it and will re-bind when it changes.
 *
 * Contract preserved
 * ------------------
 *   • `selectedTooth` is `null` unless EXACTLY one tooth is selected.
 *     This keeps the single-tooth clinical panel behaviour identical.
 *   • `isMultiSelect` is `true` for 2+ selected teeth.
 *   • `handleToothClick(fdi, evt)` reads shiftKey / ctrlKey / metaKey off
 *     the synthetic event so a single event source drives all three flows.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { PRIM_UPPER, PRIM_LOWER } from "@/components/app/ToothSVGV2";

// FDI quadrant codes → tooth string arrays (permanent dentition only —
// primary follows a different scheme and is handled via the "select all"
// button which naturally picks up the current dentition set).
const QUADRANTS = {
  Q1: ["11", "12", "13", "14", "15", "16", "17", "18"], // upper right
  Q2: ["21", "22", "23", "24", "25", "26", "27", "28"], // upper left
  Q3: ["31", "32", "33", "34", "35", "36", "37", "38"], // lower left
  Q4: ["41", "42", "43", "44", "45", "46", "47", "48"], // lower right
};

export function useDentalSelection({ allTeeth, chartTeeth }) {
  // Authoritative Set of currently-selected tooth IDs (strings).
  const [selectedTeeth, setSelectedTeeth] = useState(() => new Set());
  // Anchor for Shift+Click range selection — the last plain-clicked tooth.
  const anchorToothRef = useRef(null);
  const [selectedSurfaces, setSelectedSurfaces] = useState([]);
  const [selectedCounterOpen, setSelectedCounterOpen] = useState(false);

  // Sorted-by-FDI list of the currently selected teeth. Makes the counter
  // popover order predictable and drives multi-vs-single detection.
  const selectedTeethList = useMemo(
    () => [...selectedTeeth].sort((a, b) => Number(a) - Number(b)),
    [selectedTeeth]
  );
  const selectedTooth = selectedTeethList.length === 1 ? selectedTeethList[0] : null;
  const isMultiSelect = selectedTeethList.length > 1;

  // ---- Selection helpers -------------------------------------------------
  const selectOnly = useCallback((toothId) => {
    setSelectedTeeth(new Set([String(toothId)]));
    anchorToothRef.current = String(toothId);
    setSelectedSurfaces([]);
  }, []);

  const toggleInSelection = useCallback((toothId) => {
    setSelectedTeeth((prev) => {
      const next = new Set(prev);
      const key = String(toothId);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    anchorToothRef.current = String(toothId);
  }, []);

  const selectRangeToAnchor = useCallback((toothId) => {
    const anchor = anchorToothRef.current;
    if (!anchor) { selectOnly(toothId); return; }
    // The linear `allTeeth` array is the "range axis": consecutive FDI
    // numbers within a quadrant map to consecutive indices, and crossing
    // the midline (e.g. 13 → 23) also walks the array naturally.
    const strTeeth = allTeeth.map(String);
    const a = strTeeth.indexOf(anchor);
    const b = strTeeth.indexOf(String(toothId));
    if (a < 0 || b < 0) { selectOnly(toothId); return; }
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    const range = strTeeth.slice(lo, hi + 1);
    setSelectedTeeth((prev) => {
      const next = new Set(prev);
      range.forEach((t) => next.add(t));
      return next;
    });
  }, [allTeeth, selectOnly]);

  const clearSelection = useCallback(() => {
    setSelectedTeeth(new Set());
    setSelectedSurfaces([]);
    setSelectedCounterOpen(false);
    anchorToothRef.current = null;
  }, []);

  // Master click handler wired to every tooth. Modifier keys are read off
  // the synthetic event so one event source drives all three flows.
  const handleToothClick = useCallback((toothId, evt) => {
    if (evt?.shiftKey) {
      selectRangeToAnchor(toothId);
    } else if (evt?.ctrlKey || evt?.metaKey) {
      toggleInSelection(toothId);
    } else {
      selectOnly(toothId);
    }
  }, [selectOnly, toggleInSelection, selectRangeToAnchor]);

  // ---- Bulk selection: quadrants / arches / full mouth -------------------
  const selectSet = useCallback((teethArr) => {
    // Restrict to teeth present in the current dentition — no point
    // selecting permanent teeth when primary is displayed.
    const strAll = new Set(allTeeth.map(String));
    const clean = teethArr.map(String).filter((t) => strAll.has(t));
    setSelectedTeeth(new Set(clean));
    anchorToothRef.current = clean[0] || null;
    setSelectedSurfaces([]);
  }, [allTeeth]);

  const selectQuadrant = useCallback((q) => selectSet(QUADRANTS[q]), [selectSet]);
  const selectUpperArch = useCallback(() => selectSet([...QUADRANTS.Q1, ...QUADRANTS.Q2]), [selectSet]);
  const selectLowerArch = useCallback(() => selectSet([...QUADRANTS.Q3, ...QUADRANTS.Q4]), [selectSet]);
  const selectAllTeeth  = useCallback(() => selectSet(allTeeth.map(String)), [allTeeth, selectSet]);

  // ---- Smart filters — pick teeth by chart condition ---------------------
  const smartFilterByCodes = useCallback((codes) => {
    const strAll = new Set(allTeeth.map(String));
    const codeSet = new Set(codes);
    const matches = Object.entries(chartTeeth || {})
      .filter(([tid, t]) => strAll.has(String(tid)) &&
        (t.conditions || []).some((c) => codeSet.has(c.code)))
      .map(([tid]) => String(tid));
    if (!matches.length) {
      toast.message("No teeth match this filter.");
      return;
    }
    selectSet(matches);
  }, [chartTeeth, allTeeth, selectSet]);

  const selectAllHealthy = useCallback(() => {
    // "Healthy" = no conditions recorded on this tooth (or empty list).
    const strAll = allTeeth.map(String);
    const matches = strAll.filter((tid) => {
      const t = chartTeeth?.[tid];
      return !t || !(t.conditions || []).length;
    });
    if (!matches.length) {
      toast.message("Every tooth has a recorded condition.");
      return;
    }
    selectSet(matches);
  }, [chartTeeth, allTeeth, selectSet]);

  const selectAllDeciduous = useCallback(() => {
    // Deciduous / primary teeth. If none exist in the current view surface
    // a toast rather than silently doing nothing.
    const prim = new Set([...PRIM_UPPER, ...PRIM_LOWER].map(String));
    const matches = allTeeth.map(String).filter((t) => prim.has(t));
    if (!matches.length) {
      toast.message("Switch to Primary or Mixed dentition to select deciduous teeth.");
      return;
    }
    selectSet(matches);
  }, [allTeeth, selectSet]);

  // Invert current selection over the current dentition.
  const invertSelection = useCallback(() => {
    const strAll = allTeeth.map(String);
    const inverted = strAll.filter((t) => !selectedTeeth.has(t));
    setSelectedTeeth(new Set(inverted));
    anchorToothRef.current = inverted[0] || null;
  }, [allTeeth, selectedTeeth]);

  return {
    // state
    selectedTeeth, setSelectedTeeth,
    selectedTeethList, selectedTooth, isMultiSelect,
    selectedSurfaces, setSelectedSurfaces,
    selectedCounterOpen, setSelectedCounterOpen,
    // single-tooth flow
    selectOnly, toggleInSelection, selectRangeToAnchor, clearSelection,
    handleToothClick,
    // bulk flow
    selectQuadrant, selectUpperArch, selectLowerArch, selectAllTeeth,
    invertSelection,
    // smart filters
    smartFilterByCodes, selectAllHealthy, selectAllDeciduous,
  };
}
